import React, { useCallback, useEffect, useState } from "react";
import api from "utils/api";
import { adminBrandParams } from "utils/brand";
import { useTeamAdminBrand, useTeamAdminOrt } from "./TeamAdminBrandContext";
import { useTeamLocale } from "./TeamLocaleContext";
import { formatEuro, formatLong, sourceLabel } from "./formatters";

const sourceOptions = [
  { value: "all", labelKey: "topups.source.all" },
  { value: "stripe", labelKey: "topups.source.stripe" },
  { value: "manual", labelKey: "topups.source.manual" },
  { value: "booking-cancellation", labelKey: "topups.source.bookingCancellation" },
];

const customerName = (customer = {}) => {
  const name = [customer.firstName, customer.lastName].filter(Boolean).join(" ").trim();
  return name || customer.email || "Unbekannter Kunde";
};

const actionLabel = (t, entry) => {
  if (entry.source === "stripe") return t("topups.kind.stripe");
  if (entry.source === "booking-cancellation") {
    return t("topups.kind.bookingCancellation");
  }
  if (entry.action === "remove" || Number(entry.creditCents) < 0) {
    return t("topups.kind.adminRemove");
  }
  return t("topups.kind.adminAdd");
};

const actorLabel = (entry) => {
  if (entry.source === "stripe") return "Stripe";
  if (entry.source === "booking-cancellation") return "System";
  const admin = entry.adminUser || {};
  const name = [admin.firstName, admin.lastName].filter(Boolean).join(" ").trim();
  return name || admin.email || "Admin";
};

const BalanceTopUpsTab = () => {
  const { t, dateLocale } = useTeamLocale();
  const adminBrand = useTeamAdminBrand();
  const adminOrt = useTeamAdminOrt();
  const [topUps, setTopUps] = useState([]);
  const [totals, setTotals] = useState({});
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [source, setSource] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(
    async (page = 1, { silent = false } = {}) => {
      if (!silent) setLoading(true);
      setError(null);
      try {
        const { data } = await api.get("/admin/balance-topups", {
          params: {
            ...adminBrandParams({ brand: adminBrand, ort: adminOrt }),
            page,
            limit: 25,
            search: search || undefined,
            source,
          },
        });
        setTopUps(data.topUps || []);
        setTotals(data.totals || {});
        setPagination(data.pagination || { page, pages: 1, total: 0 });
      } catch (err) {
        if (!silent) {
          setError(err?.response?.data?.message || t("topups.loadError"));
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [adminBrand, adminOrt, search, source, t]
  );

  useEffect(() => {
    load(1, { silent: false });
  }, [load]);

  const onSearchSubmit = (event) => {
    event.preventDefault();
    setSearch(searchInput.trim());
  };

  const resetFilters = () => {
    setSearchInput("");
    setSearch("");
    setSource("all");
  };

  return (
    <div className="team-topups">
      <p className="team-lead">{t("topups.lead")}</p>

      <div className="team-topup-stats" aria-label={t("topups.statsAria")}>
        <article>
          <span>{t("topups.stats.paid")}</span>
          <strong>{formatEuro(totals.paidCents || 0)}</strong>
        </article>
        <article>
          <span>{t("topups.stats.added")}</span>
          <strong>{formatEuro(totals.creditAddedCents || 0)}</strong>
        </article>
        <article>
          <span>{t("topups.stats.bonus")}</span>
          <strong>{formatEuro(totals.bonusCents || 0)}</strong>
        </article>
        <article>
          <span>{t("topups.stats.removed")}</span>
          <strong>{formatEuro(totals.creditRemovedCents || 0)}</strong>
        </article>
      </div>

      <form className="team-toolbar" onSubmit={onSearchSubmit}>
        <input
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder={t("topups.searchPlaceholder")}
        />
        <select value={source} onChange={(event) => setSource(event.target.value)}>
          {sourceOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {t(option.labelKey)}
            </option>
          ))}
        </select>
        <button type="submit" className="auth-submit auth-submit--inline">
          {t("common.search")}
        </button>
        <button type="button" className="auth-secondary" onClick={resetFilters}>
          {t("common.reset")}
        </button>
      </form>

      {error && <div className="auth-alert is-error">{error}</div>}

      {loading ? (
        <div className="team-empty">{t("topups.loading")}</div>
      ) : topUps.length === 0 ? (
        <div className="team-empty">{t("topups.empty")}</div>
      ) : (
        <div className="team-topup-list">
          {topUps.map((entry) => {
            const creditCents = Number(entry.creditCents || 0);
            const isNegative = creditCents < 0;
            const customer = entry.customer || {};
            return (
              <article className="team-topup-card" key={entry._id}>
                <div className="team-topup-main">
                  <span className={`team-topup-badge ${isNegative ? "is-negative" : ""}`}>
                    {actionLabel(t, entry)}
                  </span>
                  <h3>{customerName(customer)}</h3>
                  <p>
                    {customer.email || "Keine E-Mail"}
                    {customer.phone ? ` · ${customer.phone}` : ""}
                  </p>
                  <p className="team-muted">
                    {sourceLabel(t, customer.source, customer.registrationSite)}
                  </p>
                </div>

                <dl className="team-topup-data">
                  <div>
                    <dt>{t("topups.field.when")}</dt>
                    <dd>{entry.creditedAt ? formatLong(entry.creditedAt, dateLocale) : "—"}</dd>
                  </div>
                  <div>
                    <dt>{t("topups.field.paid")}</dt>
                    <dd>{entry.source === "stripe" ? formatEuro(entry.paidCents || 0) : "—"}</dd>
                  </div>
                  <div>
                    <dt>{t("topups.field.credit")}</dt>
                    <dd className={isNegative ? "is-negative" : "is-positive"}>
                      {isNegative ? "-" : "+"}
                      {formatEuro(Math.abs(creditCents))}
                    </dd>
                  </div>
                  <div>
                    <dt>{t("topups.field.bonus")}</dt>
                    <dd>{entry.bonusCents ? formatEuro(entry.bonusCents) : "—"}</dd>
                  </div>
                  <div>
                    <dt>{t("topups.field.currentBalance")}</dt>
                    <dd>{formatEuro(customer.currentBalanceCents || 0)}</dd>
                  </div>
                  <div>
                    <dt>{t("topups.field.actor")}</dt>
                    <dd>{actorLabel(entry)}</dd>
                  </div>
                </dl>

                {(entry.stripeSessionId || entry.stripePaymentIntentId || entry.note) && (
                  <div className="team-topup-meta">
                    {entry.stripeSessionId && (
                      <span>Session: {entry.stripeSessionId}</span>
                    )}
                    {entry.stripePaymentIntentId && (
                      <span>PaymentIntent: {entry.stripePaymentIntentId}</span>
                    )}
                    {entry.note && <span>{entry.note}</span>}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}

      {pagination.pages > 1 && (
        <div className="team-pagination">
          <button
            type="button"
            disabled={pagination.page <= 1}
            onClick={() => load(Math.max(1, pagination.page - 1))}
          >
            {t("common.back")}
          </button>
          <span>
            {pagination.page} / {pagination.pages} · {pagination.total}
          </span>
          <button
            type="button"
            disabled={pagination.page >= pagination.pages}
            onClick={() => load(Math.min(pagination.pages, pagination.page + 1))}
          >
            {t("common.next")}
          </button>
        </div>
      )}
    </div>
  );
};

export default BalanceTopUpsTab;
